import { motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Clock,
  MapPin,
  ShieldCheck,
  Sprout,
  Video,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Reveal from "../components/Reveal";
import useSEO from "../hooks/useSEO";
import "./Servizi.css";

const servizi = [
  {
    n: "01",
    icon: MapPin,
    title: "Terapia individuale a Rieti",
    text: "Percorsi personalizzati per gestire ansia, stress, depressione e difficoltà relazionali, con incontri in presenza nello studio di Rieti.",
    cta: "Prenota un colloquio in presenza",
    to: "/prenota-appuntamento",
  },
  {
    n: "02",
    icon: Video,
    title: "Supporto psicologico online",
    text: "Sedute su Google Meet per chi preferisce la comodità di casa, con la stessa cura del setting in presenza e follow-up condivisi.",
    cta: "Richiedi una seduta online",
    to: "/prenota-appuntamento",
  },
  {
    n: "03",
    icon: Sprout,
    title: "Percorsi per l'autostima",
    text: "Un lavoro su consapevolezza, confini e autoefficacia per rafforzare il tuo valore personale, passo dopo passo.",
    cta: "Scopri il percorso",
    to: "/contatti",
  },
];

const modalita = [
  {
    icon: Clock,
    text: "Incontri di 45 minuti, in studio a Rieti o online su Meet.",
  },
  {
    icon: BookOpen,
    text: "Strumenti pratici condivisi dopo la seduta (esercizi e letture).",
  },
  {
    icon: ShieldCheck,
    text: "Massima riservatezza e consenso informato su privacy e cookie.",
  },
];

const Servizi = () => {
  const navigate = useNavigate();
  useSEO({
    title: "La Psicologa Anna | Servizi di Psicologia a Rieti e Online",
    description:
      "Terapia individuale, supporto psicologico online e percorsi di autostima con la Dott.ssa Anna Antoniozzi a Rieti.",
    canonicalPath: "/servizi",
  });

  return (
    <div className="servizi">
      {/* HERO */}
      <section className="section section--paper servizi-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="eyebrow">Studio psicologico a Rieti &amp; online</p>
            <h1 className="display servizi-hero__title">
              Servizi per il tuo{" "}
              <span className="accent-underline">benessere</span>
            </h1>
            <p className="lead servizi-hero__lead">
              Incontri in presenza e da remoto per sostenerti nelle sfide
              quotidiane e costruire un benessere duraturo.
            </p>
            <div className="servizi-hero__actions">
              <button
                className="btn btn--accent"
                onClick={() => navigate("/prenota-appuntamento")}
              >
                Prenota un appuntamento{" "}
                <ArrowRight className="btn-arrow" size={18} />
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => navigate("/contatti")}
              >
                Scrivimi
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVIZI - righe grandi */}
      <section className="section section--cream">
        <div className="container servizi-list">
          {servizi.map((s, i) => (
            <Reveal key={s.n} className="servizio-row" delay={i * 0.05}>
              <div className="servizio-row__n">
                <span>{s.n}</span>
                <span className="servizio-row__icon">
                  <s.icon size={26} strokeWidth={1.75} />
                </span>
              </div>
              <div className="servizio-row__body">
                <h2 className="display--sm">{s.title}</h2>
                <p>{s.text}</p>
                <button className="link-arrow" onClick={() => navigate(s.to)}>
                  {s.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MODALITÀ */}
      <section className="section section--paper">
        <div className="container servizi-modalita">
          <Reveal className="section-head">
            <p className="eyebrow">Come si svolgono</p>
            <h2 className="display--md">Modalità e durata</h2>
          </Reveal>
          <div className="modalita-grid">
            {modalita.map((m, i) => (
              <Reveal key={i} className="modalita-item" delay={i * 0.06}>
                <span className="modalita-icon">
                  <m.icon size={22} strokeWidth={1.75} />
                </span>
                <p>{m.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="servizi-faq-note">
              Dubbi su come funzionano gli incontri? Consulta le{" "}
              <Link to="/faq" className="link-inline">
                FAQ
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band">
        <div className="container cta-band__inner">
          <Reveal>
            <h2 className="display cta-band__title">
              Iniziamo quando te la senti.
            </h2>
            <p className="cta-band__text">
              Prenota il primo colloquio conoscitivo, senza impegno.
            </p>
            <div className="cta-band__actions">
              <Link to="/prenota-appuntamento" className="btn btn--accent">
                Prenota ora <ArrowRight className="btn-arrow" size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Servizi;
